
import { PageHeader , Badge , Button , Card, CardContent , Input , Skeleton ,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { CopyButton } from "@/components/copy-button";
import { Plus, Search } from "@/components/icons";
import { RelativeTime } from "@/components/relative-time";
import { listSites } from "@/lib/api";
import { tsToDate } from "@/lib/format";
import type { SiteListEntry, ListSitesResp } from "@/lib/types";

export function SitesListPage() {
  const { t } = useTranslation();
  const query = useQuery<ListSitesResp>({ queryKey: ["sites"], queryFn: listSites });
  const [filter, setFilter] = useState("");

  const sites = query.data?.sites;
  const filtered = useMemo(() => {
    if (!sites) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter(
      (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q),
    );
  }, [sites, filter]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("sites.eyebrow")}
        title={t("sites.title")}
        subtitle={t("sites.subtitle")}
        actions={
          <Button asChild>
            <Link to="/sites/new">
              <Plus className="h-4 w-4" />
              {t("sites.newSite")}
            </Link>
          </Button>
        }
      />

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("sites.filterPlaceholder")}
          aria-label={t("sites.filterAria")}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("sites.colName")}</TableHead>
                <TableHead>{t("sites.colSlug")}</TableHead>
                <TableHead>{t("sites.colStatus")}</TableHead>
                <TableHead>{t("sites.colExpires")}</TableHead>
                <TableHead>{t("sites.colCreated")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {query.isError && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div className="flex items-center justify-between gap-4 py-6 text-sm">
                      <span className="text-destructive">{t("sites.loadFailed")}</span>
                      <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                        {t("common.retry")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data && query.data.sites.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div className="flex flex-col items-center gap-3 py-12 text-center">
                      <p className="text-sm text-muted-foreground">{t("sites.empty")}</p>
                      <Button asChild size="sm">
                        <Link to="/sites/new">{t("sites.createFirst")}</Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data && query.data.sites.length > 0 && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <div className="py-10 text-center text-sm text-muted-foreground">
                      {t("sites.noMatch", { query: filter.trim() })}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {filtered.map((c: SiteListEntry) => (
                <TableRow key={c.siteId}>
                  <TableCell className="font-medium">
                    <Link to={`/sites/${c.siteId}`} className="hover:underline">
                      {c.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                        {c.slug}
                      </code>
                      <CopyButton value={c.slug} label={t("sites.copySlug")} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={c.archived ? "secondary" : "outline"}>
                      {c.archived ? t("sites.statusArchived") : t("sites.statusActive")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <RelativeTime date={tsToDate(c.expiresAt)} />
                  </TableCell>
                  <TableCell>
                    <RelativeTime date={tsToDate(c.createdAt)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
